import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Bot, RefreshCw, Activity, Landmark, BadgeDollarSign, XCircle, RotateCcw, MessageSquare, FilePlus2, ArrowDownUp, FileDown } from 'lucide-react';

const C = { navy: '#0D1B2A', slate: '#5C6B7A' };

const TYPES = {
  lodged: { icon: FilePlus2, color: '#1A4C7C', label: 'Lodged' },
  system: { icon: Bot, color: '#00B4D8', label: 'ARIA' },
  status: { icon: RefreshCw, color: '#1A4C7C', label: 'Status' },
  action: { icon: Activity, color: '#1A4C7C', label: 'Action' },
  reserve: { icon: Landmark, color: '#E8A020', label: 'Reserve' },
  settlement: { icon: BadgeDollarSign, color: '#1D9E75', label: 'Payment' },
  decision: { icon: XCircle, color: '#E24B4A', label: 'Decision' },
  recovery: { icon: RotateCcw, color: '#0E7490', label: 'Recovery' },
  note: { icon: MessageSquare, color: '#5C6B7A', label: 'Note' },
};

export const ClaimTimeline = ({ claim, canExport = false }) => {
  const [newestFirst, setNewestFirst] = useState(false);
  const created = new Date(claim.created_at).getTime();
  const events = (claim.notes || [])
    .map((n, i) => ({ ...n, key: i, ts: new Date(n.at).getTime() }))
    .sort((a, b) => a.ts - b.ts || a.key - b.key);
  if (events.length && !events[0].text?.startsWith('FNOL')) {
    events.unshift({ key: 'lodged', ts: created, at: claim.created_at, type: 'lodged',
      by: claim.reported_by_name || 'Broker', text: `Claim lodged — ${claim.claim_type} loss reported` });
  }
  const shown = newestFirst ? [...events].reverse() : events;

  return (
    <Card className="p-4 rounded-md mt-3" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="claim-timeline">
      <div className="flex items-center gap-2 mb-4">
        <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Claim Timeline</h3>
        <span className="text-[10px]" style={{ color: C.slate }}>{events.length} event{events.length !== 1 ? 's' : ''}</span>
        <button onClick={() => setNewestFirst(v => !v)} data-testid="timeline-sort-toggle"
          className="ml-auto flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-sm hover:bg-gray-50 transition-colors"
          style={{ color: C.slate, border: '1px solid rgba(136,150,168,0.25)' }}>
          <ArrowDownUp className="w-3 h-3" />{newestFirst ? 'Newest first' : 'Oldest first'}
        </button>
        {canExport && (
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/claim-timeline/${claim.claim_id}`}
            target="_blank" rel="noopener noreferrer" data-testid="timeline-export-pdf"
            title="Export the full chronology as a PDF for dispute files and AFCA packs"
            className="flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-sm text-white hover:opacity-90 transition-opacity"
            style={{ background: '#0D1B2A' }}>
            <FileDown className="w-3 h-3" />PDF
          </a>
        )}
      </div>
      <div className="relative">
        <div className="absolute left-[13px] top-2 bottom-2 w-px" style={{ background: 'rgba(136,150,168,0.25)' }} />
        <div className="space-y-4">
          {shown.map((e, i) => {
            const t = TYPES[e.type] || TYPES.note;
            const Icon = t.icon;
            const day = Math.max(0, Math.floor((e.ts - created) / 86400000));
            return (
              <div key={`${e.key}-${i}`} className="relative flex gap-3" data-testid={`timeline-event-${i}`}>
                <div className="relative z-10 w-[27px] h-[27px] rounded-full flex items-center justify-center shrink-0"
                  style={{ background: '#FFF', border: `2px solid ${t.color}` }}>
                  <Icon className="w-3 h-3" style={{ color: t.color }} />
                </div>
                <div className="min-w-0 pt-0.5">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm"
                      style={{ background: `${t.color}14`, color: t.color }}>{t.label}</span>
                    <span className="text-[9px] font-mono font-semibold px-1.5 py-0.5 rounded-sm"
                      style={{ background: 'rgba(13,27,42,0.05)', color: C.navy }}>Day {day}</span>
                    <span className="text-[10px]" style={{ color: C.slate }}>
                      {new Date(e.at).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <p className="text-xs mt-1 leading-relaxed" style={{ color: C.navy }}>{e.text}</p>
                  <p className="text-[10px] mt-0.5" style={{ color: C.slate }}>{e.by || 'System'}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
};
